import { PLACEHOLDER_KEYS } from "@/react/components/tearsheets/constants";
import { DropDownType } from "@/react/components/tearsheets/types";
import type { ReactNode } from "react";
import React, { useState, useCallback } from "react";
import { formatOptions } from "../helper";
import type { FormattedOption } from "@/react/prop-types";
import { CurrentAssignmentsBanner } from "@/react/components/tearsheets/assignment/current-assignments-banner";
import { ProjectStore } from "@/stores/project-store.core";
import type { ExtendedGetProjectDetailResponse } from "@/react/components/tearsheets/project/types";
import { convertUtcDateValueToDateWithoutTimezone } from "@/lib/utils/date-2";
import type {
   CurrentAssignmentsBannerProps,
   EquipmentAssignmentsBannerProps,
} from "@/react/components/tearsheets/assignment/types";
import { getTotalHours } from "@/react/components/tearsheets/helper";
import { PersonStore } from "@/stores/person-store.core";
import type { GetPersonDetailsResponse } from "@laborchart-modules/lc-core-api/dist/api/people/get-person";
import { EquipmentAssignmentBanner } from "@/react/components/tearsheets/assignment/equipment-assignments-banner";

interface DropDownState {
   categoryOptions: FormattedOption[];
   subCategoryOptions: FormattedOption[];
   categoryPlaceHolder: string;
   subCategoryPlaceHolder: string;
   currentAssignments: ReactNode;
   equipmentAssignmentsSchedules: ReactNode;
   projectProcoreId: number | null;
   projectUserIds: Array<number | null>;
   totalHours: number;
   projectStartDate: Date | null;
   projectEndDate: Date | null;
}

const initialState: DropDownState = {
   categoryOptions: [],
   subCategoryOptions: [],
   categoryPlaceHolder: PLACEHOLDER_KEYS.category.project_selected_first,
   subCategoryPlaceHolder: PLACEHOLDER_KEYS.subCategory.category_selected_first,
   currentAssignments: null,
   equipmentAssignmentsSchedules: null,
   projectProcoreId: null,
   projectUserIds: [],
   totalHours: 0,
   projectStartDate: null,
   projectEndDate: null,
};

export const useDropDownSelectionHandler = (assignmentCreationSupportData: any) => {
   const [state, setState] = useState<DropDownState>(initialState);

   const resetSubCategory = useCallback(() => {
      setState((prev) => ({
         ...prev,
         subCategoryOptions: [],
         subCategoryPlaceHolder: PLACEHOLDER_KEYS.subCategory.category_selected_first,
      }));
   }, []);

   const setTotalHours = useCallback((totalHours: number) => {
      setState((prev) => ({ ...prev, totalHours }));
   }, []);

   const handleProjectSelection = useCallback(
      async (id: any, data: any, makeTotalHoursAssignment: boolean) => {
         const newState: Partial<DropDownState> = {
            categoryOptions: [],
            subCategoryOptions: [],
            subCategoryPlaceHolder: PLACEHOLDER_KEYS.subCategory.category_selected_first,
         };

         if (id && data?.grouped_cost_code_options?.[id]) {
            const categoryOptions = formatOptions(data.grouped_cost_code_options[id]);

            newState.categoryOptions = categoryOptions;
            newState.categoryPlaceHolder = categoryOptions.length
               ? PLACEHOLDER_KEYS.category.select_category
               : PLACEHOLDER_KEYS.category.project_no_options;

            if (categoryOptions.length === 0) {
               newState.subCategoryOptions = [];
               newState.subCategoryPlaceHolder = PLACEHOLDER_KEYS.subCategory.category_no_options;
            }

            const projectData: ExtendedGetProjectDetailResponse =
               await ProjectStore.getProjectDetails(id).payload;

            newState.projectStartDate =
               projectData.data.start_date != null
                  ? convertUtcDateValueToDateWithoutTimezone(projectData.data.start_date)
                  : null;

            newState.projectEndDate =
               projectData.data.est_end_date != null
                  ? convertUtcDateValueToDateWithoutTimezone(projectData.data.est_end_date)
                  : null;

            newState.projectProcoreId =
               projectData.data.procore_id != null ? Number(projectData.data.procore_id) : null;

            if (makeTotalHoursAssignment) {
               newState.totalHours = getTotalHours(
                  projectData.data.daily_end_time,
                  projectData.data.daily_start_time,
               );
            }
         } else {
            newState.categoryPlaceHolder = PLACEHOLDER_KEYS.category.project_no_options;
         }
         return newState;
      },
      [],
   );

   const handleCategorySelection = useCallback((id: any, data: any) => {
      if (id && data?.grouped_label_options?.[id]) {
         const subCategoryOptions = formatOptions(data.grouped_label_options[id]);
         return {
            subCategoryOptions,
            subCategoryPlaceHolder: subCategoryOptions.length
               ? PLACEHOLDER_KEYS.subCategory.select_subcategory
               : PLACEHOLDER_KEYS.subCategory.category_no_options,
         };
      }
      return {
         subCategoryOptions: [],
         subCategoryPlaceHolder: PLACEHOLDER_KEYS.subCategory.category_no_options,
      };
   }, []);

   const handleResourceSelection = useCallback(async (option: any) => {
      let bannerProps: CurrentAssignmentsBannerProps = {
         variant: "info",
         personIds: [],
         icon: "info",
         personNames: [],
         showBanner: false,
      };

      let projectUserIds: Array<number | null> = [];

      if (Array.isArray(option) && option.length > 0) {
         const personIds = option.map(({ id }) => id);
         const personNames = option.map(({ label }) => label);

         bannerProps = {
            ...bannerProps,
            personIds,
            personNames,
            showBanner: true,
         };

         // Fetch person details for each selected person
         projectUserIds = await Promise.all(
            personIds.map(async (id) => {
               const personData: GetPersonDetailsResponse = await PersonStore.getPersonDetails(id)
                  .payload;
               return personData.data.procore_person_id ?? null;
            }),
         );
      }

      return {
         currentAssignments: <CurrentAssignmentsBanner {...bannerProps} />,
         projectUserIds,
      };
   }, []);

   const handleEquipmentSelection = useCallback((option: any) => {
      let bannerProps: EquipmentAssignmentsBannerProps = {
         variant: "info",
         equipmentResourceId: "",
         equipmentName: "",
         equipmentIdentificationNumber: "",
         icon: "info",
         showBanner: false,
      };

      if (option) {
         const { id, name, identificationNumber } = option;
         bannerProps = {
            ...bannerProps,
            equipmentResourceId: id,
            equipmentName: name,
            equipmentIdentificationNumber: identificationNumber,
            showBanner: true,
         };
      }

      return {
         equipmentAssignmentsSchedules: <EquipmentAssignmentBanner {...bannerProps} />,
      };
   }, []);

   const handleSelection = useCallback(
      async (option: any, type: string, makeTotalHoursAssignment: boolean = true) => {
         const { id } = option || {};
         const { data } = assignmentCreationSupportData ?? {};

         if (!data) return;

         let newState: Partial<DropDownState> = {};

         switch (type) {
            case DropDownType.PROJECT:
               newState = await handleProjectSelection(id, data, makeTotalHoursAssignment);
               break;

            case DropDownType.CATEGORY:
               newState = handleCategorySelection(id, data);
               break;

            case DropDownType.RESOURCE:
               newState = await handleResourceSelection(option);
               break;

            case DropDownType.EQUIPMENT:
               newState = handleEquipmentSelection(option);
               break;

            default:
               return;
         }

         setState((prev) => ({ ...prev, ...newState }));
      },
      [
         assignmentCreationSupportData,
         handleProjectSelection,
         handleCategorySelection,
         handleResourceSelection,
         handleEquipmentSelection,
      ],
   );

   return {
      ...state,
      handleSelection,
      resetSubCategory,
      setTotalHours,
   };
};
